import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  ArrowRight,
  BookOpen,
  Bot,
  Blocks,
  Check,
  CheckCircle2,
  Crown,
  Loader2,
  MessageCircle,
  Rocket,
  Send,
  Zap,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { createPlatformUser } from '@/services/native-client-platform'
import {
  CARD_STYLE,
  COLORS,
  INPUT_CLASS,
  INPUT_STYLE,
  ONBOARDING_SEEN_KEY,
  clearOnboardingState,
} from '../shared'

const CONFETTI_COLORS = ['#AD34FE', '#34ADFE', '#FE9F34', '#FE34AD', '#34FEAD']

function ConfettiPiece({ index }) {
  const left = (index * 7.3) % 100
  const delay = (index % 9) * 0.2
  const duration = 3 + ((index * 0.4) % 2)
  const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length]
  const size = 6 + ((index * 1.5) % 6)

  return (
    <span
      aria-hidden
      className="confetti-piece"
      style={{
        left: `${left}%`,
        width: `${size}px`,
        height: `${size}px`,
        background: color,
        animationDelay: `${delay}s`,
        animationDuration: `${duration}s`,
      }}
    />
  )
}

export default function Launch({
  mode,
  user,
  createdPlatform,
  createdAssistant,
  integrationStatus,
  setupProgress,
  onFinish,
}) {
  const navigate = useNavigate()
  const isAgentFlow = mode === 'agent'
  const firstName = user?.name?.split(' ')[0] || ''
  const platformName = createdPlatform?.name || 'Cliente'
  const agentName = createdAssistant?.name || 'Agente'
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [invited, setInvited] = useState(false)
  const confetti = useMemo(() => Array.from({ length: 28 }, (_, index) => index), [])
  const knowledgeCount = Number(setupProgress?.knowledgeCount) || 0
  const templateCount = setupProgress?.templateIds?.length || 0

  useEffect(() => {
    onFinish?.()
  }, [onFinish])

  const checklist = [
    { icon: Crown, label: 'Cliente definido', done: Boolean(createdPlatform?.id), accent: '#FE9F34' },
    { icon: Bot, label: 'Agente vinculado', done: Boolean(createdAssistant?.id && createdAssistant?.linked), accent: '#AD34FE' },
    {
      icon: BookOpen,
      label: knowledgeCount ? `${knowledgeCount} ${knowledgeCount === 1 ? 'fonte de conhecimento' : 'fontes de conhecimento'}` : 'Conhecimento para adicionar',
      done: knowledgeCount > 0,
      accent: '#34ADFE',
    },
    {
      icon: Zap,
      label: templateCount ? `${templateCount} ${templateCount === 1 ? 'template instalado' : 'templates instalados'}` : 'Templates opcionais',
      done: templateCount > 0,
      accent: '#FE9F34',
    },
    {
      icon: MessageCircle,
      label: integrationStatus?.channelLabel || 'Canal opcional',
      done: Boolean(integrationStatus?.channel),
      accent: '#34FEAD',
    },
  ]

  async function handleInvite() {
    const email = inviteEmail.trim()
    if (!email || !createdPlatform?.id) {
      toast.error('Informe um e-mail válido para o cliente.')
      return
    }

    setInviting(true)
    try {
      await createPlatformUser(createdPlatform.id, { email })
      setInvited(true)
      toast.success(`Convite enviado para ${email}!`)
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível enviar o convite.')
    } finally {
      setInviting(false)
    }
  }

  function handleOpenAgent() {
    clearOnboardingState()
    localStorage.setItem(ONBOARDING_SEEN_KEY, '1')
    navigate(createdAssistant?.id ? `/assistants/${createdAssistant.id}` : '/')
  }

  function handleGoDashboard() {
    clearOnboardingState()
    localStorage.setItem(ONBOARDING_SEEN_KEY, '1')
    navigate('/')
  }

  return (
    <div className="relative mx-auto flex max-w-3xl flex-col items-center gap-8 text-center sm:gap-10">
      <div className="pointer-events-none absolute inset-x-0 -top-10 h-[420px] overflow-hidden">
        {confetti.map((index) => <ConfettiPiece key={index} index={index} />)}
      </div>

      <div className="relative">
        <div className="absolute inset-0 -z-10 rounded-full blur-3xl" style={{ background: 'radial-gradient(circle, #AD34FE, transparent 70%)', opacity: 0.4 }} />
        <div className="flex h-20 w-20 items-center justify-center rounded-3xl sm:h-24 sm:w-24" style={{ background: 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)', boxShadow: '0 0 64px rgba(173,52,254,0.55), 0 24px 48px rgba(0,0,0,0.5)' }}>
          <Rocket className="h-10 w-10 text-white sm:h-12 sm:w-12" strokeWidth={2.2} />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <h2 className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: COLORS.text, lineHeight: 1.05 }}>
          <span style={{ background: 'linear-gradient(90deg, #FE9F34, #AD34FE, #34ADFE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
            {agentName}
          </span>
          <br />
          foi criado com sucesso.
        </h2>
        <p className="text-sm sm:text-base" style={{ color: COLORS.textMuted }}>
          {firstName ? `${firstName}, ` : ''}a configuração inicial está salva. Revise o agente antes de ativá-lo em produção.
        </p>
      </div>

      <div className="w-full rounded-2xl p-5 sm:rounded-3xl sm:p-6" style={CARD_STYLE}>
        <div className="flex items-center gap-3 border-b pb-4 text-left" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, #FE9F34 0%, #AD34FE 100%)' }}>
            <Crown className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold" style={{ color: COLORS.text }}>{platformName}</p>
            <p className="text-[11px]" style={{ color: COLORS.textMuted }}>Resumo da configuração inicial</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-2 pt-4 sm:grid-cols-2 lg:grid-cols-5">
          {checklist.map((item) => {
            const Icon = item.icon
            return (
              <div key={item.label} className="flex items-center gap-2 rounded-xl p-3 text-left" style={{ background: `${item.accent}10`, border: `1px solid ${item.accent}35` }}>
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg" style={{ background: `${item.accent}20`, border: `1px solid ${item.accent}40` }}>
                  {item.done ? <Check className="h-3.5 w-3.5" style={{ color: item.accent }} strokeWidth={3} /> : <Icon className="h-3.5 w-3.5" style={{ color: item.accent }} />}
                </div>
                <span className="text-[11px] font-medium leading-tight" style={{ color: COLORS.text }}>{item.label}</span>
              </div>
            )
          })}
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-xl px-3 py-2.5 text-left" style={{ background: 'rgba(254,159,52,0.08)', border: '1px solid rgba(254,159,52,0.22)' }}>
          <Blocks className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#FE9F34]" />
          <p className="text-[11px] leading-relaxed" style={{ color: '#FFE0AE' }}>
            O agente permanece inativo até você revisar as instruções, testar a conversa e decidir ativá-lo.
          </p>
        </div>
      </div>

      {!isAgentFlow && (
        <div className="w-full rounded-2xl p-5 text-left sm:rounded-3xl sm:p-6" style={CARD_STYLE}>
          <label className="text-[10px] font-semibold uppercase tracking-wider sm:text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Convide o cliente para acompanhar a operação
          </label>
          <div className="mt-3 flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="email@cliente.com"
              className={INPUT_CLASS}
              style={INPUT_STYLE}
              disabled={invited}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !invited) handleInvite()
              }}
            />
            <button type="button" onClick={handleInvite} disabled={inviting || invited || !inviteEmail.trim()} className="flex h-12 shrink-0 items-center justify-center gap-2 rounded-xl px-5 text-sm font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-40" style={invited ? { background: 'rgba(52,254,173,0.12)', border: '1px solid rgba(52,254,173,0.4)', color: '#34FEAD' } : { background: 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)', color: '#fff' }}>
              {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : invited ? <CheckCircle2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              {invited ? 'Enviado' : 'Convidar'}
            </button>
          </div>
        </div>
      )}

      <div className="flex w-full flex-col gap-3 sm:flex-row sm:justify-center">
        <button type="button" onClick={handleOpenAgent} className="group flex h-14 flex-1 items-center justify-center gap-3 rounded-2xl px-6 text-base font-bold transition-all duration-200 active:scale-[0.98] sm:max-w-xs" style={{ background: 'linear-gradient(135deg, #AD34FE 0%, #7b1fd4 100%)', color: '#fff', boxShadow: '0 0 48px rgba(173,52,254,0.45), 0 8px 24px rgba(0,0,0,0.4)' }}>
          Abrir agente
          <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
        </button>
        <button type="button" onClick={handleGoDashboard} className="flex h-14 flex-1 items-center justify-center gap-2 rounded-2xl px-6 text-sm font-semibold transition-all duration-200 active:scale-[0.98] sm:max-w-xs" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)' }}>
          Ir para a central de clientes
        </button>
      </div>

      <style>{`
        .confetti-piece { position: absolute; top: -20px; border-radius: 2px; opacity: 0; animation-name: confetti-fall; animation-iteration-count: infinite; animation-timing-function: linear; }
        @keyframes confetti-fall { 0% { transform: translateY(0) rotate(0deg); opacity: 0; } 10% { opacity: 1; } 100% { transform: translateY(420px) rotate(720deg); opacity: 0; } }
      `}</style>
    </div>
  )
}
